import React, { ReactNode, useEffect, useRef } from "react";
import { Dialog } from "@vitalsource/vstui";
import FullScreenDialog, {
  FullScreenDialogProps,
} from "../../../../../common/ui/FullScreenDialog";
import { assertUnreachable } from "../../../../../common/utils/assertions";
import { useDialogManager } from "./dialogs";
import { useActiveDialog } from "../../../framework/connectors/active-dialog/useActiveDialog";
import ReactDOM from "react-dom";
import { getFocusableElements } from "../../../../../common/utils/dom";

/*
  All reader dialogs should use this wrapper component to manage "responsive dialogs"
  Responsive dialogs are panel contents that are either inline with the reader frame UI
  or full screen modal depending on media queries. This is an aspect of "compact view"
  and supports a11y users who require high zoom levels on desktop. c.f. Left/RightBar
  Essentially if a responsive modal is full screen and focus trapped, when a child
  dialog launches the responsive modal backs out (to relinquish focus trap and z-index)
  returning again when the child closes. This is done via the global setActiveDialog
  Note a responsive modal can go full screen (but inactive) behind an active dialog
  if a media break occurs later, e.g. right panel open > dialog launched -> user zooms
 */

export enum ReaderDialogType {
  /* use the standard vstui dialog component (probably this is what you want) */
  Vstui = "vstui",
  /* if you need a custom dialog outside vstui's scope then choose this */
  FullScreen = "fullScreen",
  /* use this if you have a panel that flips inline to fullscreen depending on compact view media query. */
  ResponsiveModal = "responsiveModal",
}

interface ReaderDialogBaseProps {
  type: ReaderDialogType;
  open: boolean;
  children: ReactNode;
  className?: string;
}

type VstuiReaderDialogProps = ReaderDialogBaseProps & {
  type: ReaderDialogType.Vstui;
  // note vstui types needed as leaving open breaks discrimination
  onRequestClose?: () => void;
  primaryActionButton?: { label: string; onClick: () => void };
  secondaryActionButton?: { label: string; onClick: () => void };
  size?: "small" | "large";
  title: string;
  alert?: boolean;
  darkHeader?: boolean;
  requireInteraction?: boolean;
  customSize?: number;
  fullInnerWidth?: boolean;
};

type FullScreenReaderDialogProps = ReaderDialogBaseProps & {
  type: ReaderDialogType.FullScreen;
} & FullScreenDialogProps;

type ResponsiveModalReaderDialogProps = Omit<
  FullScreenReaderDialogProps,
  "type" | "inactive"
> & {
  type: ReaderDialogType.ResponsiveModal;
};

type WithoutDialogType<T> = Omit<T, "type">;

type ReaderDialogProps =
  | VstuiReaderDialogProps
  | FullScreenReaderDialogProps
  | ResponsiveModalReaderDialogProps;

const ReaderDialog = React.forwardRef<any, ReaderDialogProps>((props, ref) => {
  // note props spread after discrimination
  switch (props.type) {
    case ReaderDialogType.Vstui: {
      const { type, ...otherProps } = props;
      return <ReaderVstuiDialog ref={ref} {...otherProps} />;
    }
    case ReaderDialogType.FullScreen: {
      const { type, ...otherProps } = props;
      return <ReaderFullScreenDialog {...otherProps} />;
    }
    case ReaderDialogType.ResponsiveModal:
      const { type, ...otherProps } = props;
      return <ReaderResponsiveModalDialog {...otherProps} />;
    default:
      return assertUnreachable(props);
  }
});

const ReaderVstuiDialog = React.forwardRef<
  any,
  WithoutDialogType<VstuiReaderDialogProps>
>((props, ref) => {
  const localRef = useRef();
  useDialogManager(props.open);

  useEffect(() => {
    // when launched from "responsive modal" vstui mount focus
    // was patchy, so make sure (maybe interference from  a11y-dialog closing)
    if (props.open && ((ref as any)?.current || localRef?.current)) {
      const container = ReactDOM.findDOMNode(
        (ref as any)?.current || localRef.current
      ) as HTMLDivElement;
      setTimeout(() => {
        getFocusableElements(container)[0]?.focus();
      }, 1);
    }
  }, [props.open, ref]);

  return <Dialog ref={ref || localRef} {...props} />;
});

const ReaderFullScreenDialog: React.FC<WithoutDialogType<
  FullScreenReaderDialogProps
>> = (props) => {
  useDialogManager(props.open);

  return <FullScreenDialog {...props} />;
};

const ReaderResponsiveModalDialog: React.FC<WithoutDialogType<
  ResponsiveModalReaderDialogProps
>> = (props) => {
  const { activeDialog } = useActiveDialog();

  return <FullScreenDialog {...props} inactive={activeDialog} />;
};

export default ReaderDialog;
